import { useState } from 'react';
import { MapPin, X, ExternalLink } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface LocationValue {
  address: string;
  lat?: number;
  lng?: number;
}

interface LocationCellProps {
  value: LocationValue | null;
  onChange: (value: LocationValue | null) => void;
  readOnly?: boolean;
}

export function LocationCell({ value, onChange, readOnly }: LocationCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempAddress, setTempAddress] = useState(value?.address || '');

  const handleSave = () => {
    const trimmed = tempAddress.trim();
    if (!trimmed) {
      onChange(null);
      setIsEditing(false);
      return;
    }

    onChange({
      address: trimmed,
    });
    setIsEditing(false);
  };

  const handleClear = () => {
    setTempAddress('');
    onChange(null);
    setIsEditing(false);
  };

  const getGoogleMapsUrl = (address: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  if (readOnly && !value) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  if (readOnly && value) {
    return (
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-red-500" />
        <span className="truncate">{value.address}</span>
        <a
          href={getGoogleMapsUrl(value.address)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-2 p-2 bg-white border rounded shadow-lg min-w-[300px]">
        <Input
          placeholder="Endereço completo"
          value={tempAddress}
          onChange={(e) => setTempAddress(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          autoFocus
        />
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave}>
            Salvar
          </Button>
          <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
            Cancelar
          </Button>
          {value && (
            <Button size="sm" variant="ghost" onClick={handleClear}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!value) {
    return (
      <div
        className="text-gray-400 text-sm cursor-pointer hover:text-gray-600"
        onClick={() => setIsEditing(true)}
      >
        Adicionar localização
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
      onClick={() => setIsEditing(true)}
    >
      <MapPin className="w-4 h-4 text-red-500" />
      <span className="truncate">{value.address}</span>
      <a
        href={getGoogleMapsUrl(value.address)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}
